import React from "react";

const ALLOWED_TAGS = new Set(["P", "BR", "STRONG", "B", "EM", "I", "U", "UL", "OL", "LI", "SPAN", "A", "DIV"]);
const ALLOWED_STYLES = new Set(["color", "font-size", "font-weight", "font-style", "text-decoration"]);

function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

export function normalizeCommentHtml(value = "") {
  if (!value) return "";
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (/<[a-z][\s\S]*>/i.test(trimmed)) return trimmed;
  return trimmed
    .split(/\n{2,}/)
    .map((block) => `<p>${escapeHtml(block).replace(/\n/g, "<br />")}</p>`)
    .join("");
}

export function extractPlainTextFromHtml(value = "") {
  if (!value) return "";
  if (typeof document === "undefined") {
    return value.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
  }
  const doc = document.implementation.createHTMLDocument("");
  doc.body.innerHTML = normalizeCommentHtml(value);
  return (doc.body.textContent || "").replace(/\s+/g, " ").trim();
}

export function extractMentionsFromHtml(value = "", users = []) {
  if (!value) return [];
  if (typeof document !== "undefined") {
    const doc = document.implementation.createHTMLDocument("");
    doc.body.innerHTML = normalizeCommentHtml(value);
    const usersById = new Map(users.map((user) => [String(user.id), user]));
    const foundById = new Map();

    Array.from(doc.body.querySelectorAll(".rc-comment-mention[data-mention-id]")).forEach((node) => {
      const mentionId = node.getAttribute("data-mention-id");
      const mentionType = (node.getAttribute("data-mention-type") || "user").toLowerCase();
      if (!mentionId || mentionType !== "user") return;
      const matched = usersById.get(String(mentionId));
      if (matched) foundById.set(String(matched.id), matched);
    });

    if (foundById.size > 0) return Array.from(foundById.values());
  }

  const plainText = extractPlainTextFromHtml(value);
  if (!plainText) return [];
  const found = [];
  let remaining = plainText;
  while (remaining.length > 0) {
    const atIndex = remaining.indexOf("@");
    if (atIndex === -1) break;
    const afterAt = remaining.slice(atIndex + 1);
    const matched = users.find((user) => afterAt.toLowerCase().startsWith((user.name || "").toLowerCase()));
    if (matched) {
      if (!found.find((entry) => entry.id === matched.id)) found.push(matched);
      remaining = afterAt.slice((matched.name || "").length);
    } else {
      remaining = afterAt;
    }
  }
  return found;
}

export function extractMentionMetadataFromHtml(value = "") {
  if (!value) return [];
  if (typeof document === "undefined") return [];
  const doc = document.implementation.createHTMLDocument("");
  doc.body.innerHTML = normalizeCommentHtml(value);
  const found = new Map();

  Array.from(doc.body.querySelectorAll(".rc-comment-mention[data-mention-id]")).forEach((node) => {
    const mentionId = node.getAttribute("data-mention-id");
    if (!mentionId) return;
    found.set(mentionId, {
      id: mentionId,
      name: node.getAttribute("data-mention-name") || (node.textContent || "").replace(/^@+/, ""),
      type: (node.getAttribute("data-mention-type") || "user").toLowerCase(),
    });
  });

  return Array.from(found.values());
}

export function extractCommentAttachments(value = "") {
  if (!value || typeof document === "undefined") return [];
  const doc = document.implementation.createHTMLDocument("");
  doc.body.innerHTML = normalizeCommentHtml(value);
  return Array.from(doc.body.querySelectorAll("[data-rc-attachment='true']")).map((link, index) => ({
    id: link.getAttribute("data-attachment-id") || `embedded-${index}`,
    fileName: link.getAttribute("data-file-name") || link.textContent || "Attachment",
    fileUrl: link.getAttribute("href") || "",
  })).filter((item) => item.fileUrl);
}

function sanitizeNode(node, doc) {
  if (node.nodeType === Node.TEXT_NODE) {
    return doc.createTextNode(node.textContent || "");
  }
  if (node.nodeType !== Node.ELEMENT_NODE) return null;

  const tagName = node.tagName.toUpperCase();
  if (!ALLOWED_TAGS.has(tagName)) {
    const fragment = doc.createDocumentFragment();
    Array.from(node.childNodes).forEach((child) => {
      const sanitizedChild = sanitizeNode(child, doc);
      if (sanitizedChild) fragment.appendChild(sanitizedChild);
    });
    return fragment;
  }

  const clean = doc.createElement(tagName.toLowerCase());

  if (tagName === "SPAN" && node.classList.contains("rc-comment-mention")) {
    clean.className = "rc-comment-mention";
    const mentionId = node.getAttribute("data-mention-id");
    if (mentionId) clean.setAttribute("data-mention-id", mentionId);
    const mentionName = node.getAttribute("data-mention-name");
    if (mentionName) clean.setAttribute("data-mention-name", mentionName);
    const mentionType = node.getAttribute("data-mention-type");
    if (mentionType) clean.setAttribute("data-mention-type", mentionType);
  }

  if (tagName === "A") {
    const href = node.getAttribute("href") || "";
    const isSafeHref = /^(https?:|mailto:|data:)/i.test(href);
    if (isSafeHref) clean.setAttribute("href", href);
    if (node.getAttribute("download")) clean.setAttribute("download", node.getAttribute("download"));
    if (node.getAttribute("data-rc-attachment") === "true") {
      clean.setAttribute("data-rc-attachment", "true");
      if (node.getAttribute("data-attachment-id")) clean.setAttribute("data-attachment-id", node.getAttribute("data-attachment-id"));
      if (node.getAttribute("data-file-name")) clean.setAttribute("data-file-name", node.getAttribute("data-file-name"));
      clean.className = "rc-comment-attachment-link";
    }
    clean.setAttribute("target", "_blank");
    clean.setAttribute("rel", "noreferrer");
  }

  const style = node.getAttribute("style");
  if (style) {
    const safeStyle = style
      .split(";")
      .map((entry) => entry.trim())
      .filter(Boolean)
      .filter((entry) => ALLOWED_STYLES.has(entry.split(":")[0]?.trim().toLowerCase()));
    if (safeStyle.length) clean.setAttribute("style", safeStyle.join("; "));
  }

  Array.from(node.childNodes).forEach((child) => {
    const sanitizedChild = sanitizeNode(child, doc);
    if (sanitizedChild) clean.appendChild(sanitizedChild);
  });

  return clean;
}

export function sanitizeCommentHtml(value = "") {
  if (!value || typeof document === "undefined") return normalizeCommentHtml(value);
  const source = document.implementation.createHTMLDocument("");
  source.body.innerHTML = normalizeCommentHtml(value);
  const clean = document.implementation.createHTMLDocument("");
  Array.from(source.body.childNodes).forEach((child) => {
    const sanitizedChild = sanitizeNode(child, clean);
    if (sanitizedChild) clean.body.appendChild(sanitizedChild);
  });
  return clean.body.innerHTML;
}

export function stripMentionMarkupFromHtml(value = "") {
  if (!value || typeof document === "undefined") return normalizeCommentHtml(value);
  const source = document.implementation.createHTMLDocument("");
  source.body.innerHTML = normalizeCommentHtml(value);
  Array.from(source.body.querySelectorAll(".rc-comment-mention")).forEach((node) => {
    const displayName = node.getAttribute("data-mention-name") || (node.textContent || "").replace(/^@+/, "");
    node.replaceWith(source.createTextNode(`@${displayName}`));
  });
  return source.body.innerHTML;
}

export function renderCommentContent(value = "") {
  return {
    __html: sanitizeCommentHtml(value),
  };
}

export function buildAttachmentMarkup(attachments = []) {
  if (!attachments.length) return "";
  const items = attachments.map((attachment) => (
    `<li><a class="rc-comment-attachment-link" data-rc-attachment="true" data-attachment-id="${escapeHtml(attachment.id)}" data-file-name="${escapeHtml(attachment.fileName)}" href="${escapeHtml(attachment.fileUrl)}" download="${escapeHtml(attachment.fileName)}">${escapeHtml(attachment.fileName)}</a></li>`
  )).join("");
  return `<div class="rc-comment-attachments"><div class="rc-comment-attachments-title">Attachments</div><ul>${items}</ul></div>`;
}

export function CommentContent({ value, className = "" }) {
  return <div className={className} dangerouslySetInnerHTML={renderCommentContent(value)} />;
}
